import React, { useEffect, useState } from 'react';
import { userService } from '../services';

interface SpacesCredential {
  id: number;
  name: string;
  access_key_id: string;
  region: string;
  endpoint: string;
  created_at: string;
}

export function SpacesCredentials() {
  const [creds, setCreds] = useState<SpacesCredential[]>([]);
  const [name, setName] = useState('');
  const [accessKeyId, setAccessKeyId] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [region, setRegion] = useState('nyc3');
  const [endpoint, setEndpoint] = useState('https://nyc3.digitaloceanspaces.com');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCreds();
  }, []);

  const loadCreds = async () => {
    try {
      setLoading(true);
      const list = await userService.getSpacesCredentials();
      setCreds(list);
    } catch (e: any) {
      setError(e?.message || 'Failed to load credentials');
    } finally {
      setLoading(false);
    }
  };

  const onAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await userService.createSpacesCredential({ name, access_key_id: accessKeyId, secret_key: secretKey, region, endpoint });
      setName(''); setAccessKeyId(''); setSecretKey('');
      await loadCreds();
    } catch (err: any) {
      setError(err?.message || 'Failed to add credential');
    }
  };

  const onDelete = async (id: number) => {
    setError(null);
    try {
      await userService.deleteSpacesCredential(id);
      await loadCreds();
    } catch (err: any) {
      setError(err?.message || 'Failed to delete credential');
    }
  };

  return (
    <div className="p-8 text-white bg-gray-900 min-h-screen">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">Spaces Credentials</h1>
        <p className="text-gray-400 mb-6">Store your DigitalOcean Spaces access keys securely and reuse them across Spaces-based labs. Secrets are encrypted at rest and never shown after creation.</p>

        {error && (
          <div className="mb-4 bg-red-900/30 border border-red-700 text-red-200 p-3 rounded">{error}</div>
        )}

        <form onSubmit={onAdd} className="bg-gray-800 border border-gray-700 rounded p-4 mb-8">
          <h2 className="text-xl font-semibold mb-4">Add Credential</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-300 mb-1">Name</label>
              <input value={name} onChange={e => setName(e.target.value)} className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2" required />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Access Key ID</label>
              <input value={accessKeyId} onChange={e => setAccessKeyId(e.target.value)} className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2" required />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Secret Key</label>
              <input type="password" value={secretKey} onChange={e => setSecretKey(e.target.value)} className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2" required />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Region</label>
              <input value={region} onChange={e => setRegion(e.target.value)} className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm text-gray-300 mb-1">Endpoint</label>
              <input value={endpoint} onChange={e => setEndpoint(e.target.value)} className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2" />
            </div>
          </div>
          <div className="mt-4">
            <button type="submit" className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded">Add Credential</button>
          </div>
        </form>

        <h2 className="text-xl font-semibold mb-3">Saved Credentials</h2>
        {loading ? (
          <div className="text-gray-400">Loading…</div>
        ) : creds.length === 0 ? (
          <div className="text-gray-400">No credentials saved yet.</div>
        ) : (
          <div className="space-y-2">
            {creds.map(c => (
              <div key={c.id} className="bg-gray-800 border border-gray-700 rounded p-4 flex items-center justify-between">
                <div>
                  <div className="font-semibold">{c.name}</div>
                  <div className="text-gray-400 text-sm">{c.access_key_id} · {c.region} · {c.endpoint}</div>
                </div>
                <button onClick={() => onDelete(c.id)} className="px-3 py-1 bg-red-600 hover:bg-red-500 rounded text-white text-sm">Delete</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

